import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Header, 
  Spinner, 
  SpaceBetween,
  Box,
  Badge,
  Button,
  ColumnLayout
} from '@cloudscape-design/components';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const ClassroomMessages = ({ chatrooms = [], onRefresh, refreshTrigger }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedChatrooms, setExpandedChatrooms] = useState({}); // 跟踪哪些群组是展开的
  const [countdown, setCountdown] = useState(300); // 倒计时秒数(5分钟=300秒)
  const countdownIntervalRef = React.useRef(null);

  // 初始化时自动展开所有群组
  useEffect(() => {
    if (chatrooms && chatrooms.length > 0) {
      const initialExpanded = {};
      chatrooms.forEach(chatroom => {
        initialExpanded[chatroom.fromWxid] = true; // 默认全部展开
      });
      setExpandedChatrooms(initialExpanded);
      fetchClassroomMessages();
    }
  }, [chatrooms]);

  // 监听refreshTrigger变化时刷新消息并重置倒计时
  useEffect(() => {
    if (refreshTrigger > 0 && chatrooms && chatrooms.length > 0) {
      fetchClassroomMessages();
      setCountdown(300); // 重置倒计时
    }
  }, [refreshTrigger]);

  // 倒计时逻辑
  useEffect(() => {
    // 清除旧的倒计时定时器
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    // 设置新的倒计时定时器 - 每秒减1
    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          return 300; // 倒计时结束后重置为5分钟
        }
        return prev - 1;
      });
    }, 1000);

    // 组件卸载时清除定时器
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  const fetchClassroomMessages = async () => {
    setLoading(true);
    try {
      // 为每个群聊获取当天所有消息
      const promises = chatrooms.map(chatroom => 
        axios.get(`${API_BASE_URL}/classroom-messages/${chatroom.fromWxid}`)
      );
      
      const responses = await Promise.all(promises);
      const messagesData = responses.map((response, index) => {
        const data = response.data;
        const allMessages = data.messages || [];
        // 获取最后一条消息（最新的）
        const latestMessage = allMessages.length > 0 ? allMessages[allMessages.length - 1] : null;
        
        return {
          ...chatrooms[index],
          fromWxid: data.fromWxid,
          messages: allMessages,
          total: data.total,
          // 显示用的最新消息
          msg: latestMessage ? latestMessage.msg : '暂无消息',
          timeStamp: latestMessage ? latestMessage.timeStamp : '',
          unreadCount: 0  // 可以根据需要计算未读数
        };
      });
      
      setMessages(messagesData);
    } catch (error) {
      console.error('获取班级群消息失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshClick = () => {
    fetchClassroomMessages();
    setCountdown(300); // 重置倒计时
    // 同时触发父组件的刷新（任务列表）
    if (onRefresh) {
      onRefresh();
    }
  };

  // 格式化倒计时显示
  const formatCountdown = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const toggleChatroom = (fromWxid) => {
    setExpandedChatrooms(prev => ({
      ...prev,
      [fromWxid]: !prev[fromWxid]
    }));
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(parseInt(timestamp));
    return date.toLocaleString('zh-CN', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 解析XML消息，提取title内容
  const parseXmlMessage = (msg) => {
    if (!msg || typeof msg !== 'string') return msg;
    
    // 判断是否为XML格式（以<?xml开头或包含<msg>标签）
    if (msg.trim().startsWith('<?xml') || msg.includes('<msg>')) {
      try {
        // 使用正则提取title标签内容（包括CDATA）
        const titleMatch = msg.match(/<title><!\[CDATA\[(.+?)\]\]><\/title>/);
        if (titleMatch && titleMatch[1]) {
          return titleMatch[1];
        }
        
        // 如果没有CDATA，直接提取title内容
        const simpleTitleMatch = msg.match(/<title>(.+?)<\/title>/);
        if (simpleTitleMatch && simpleTitleMatch[1]) {
          return simpleTitleMatch[1];
        }
      } catch (error) {
        console.error('解析XML消息失败:', error);
      }
    }
    
    // 如果不是XML或解析失败，返回原消息
    return msg;
  };

  // 渲染消息内容（支持图片和文本）
  const renderMessageContent = (msg) => {
    if (!msg || typeof msg !== 'string') {
      return <Box variant="p" color="text-body-secondary">暂无消息</Box>;
    }

    // 检查是否为未解密图片
    if (msg === '[未解密图片]') {
      return (
        <Box variant="p" color="text-status-warning">
          🔒 未解密图片
        </Box>
      );
    }

    // 检查是否为图片消息 [image]base64字符串
    if (msg.startsWith('[image]')) {
      const base64Data = msg.substring(7); // 去掉 [image] 前缀
      
      // 如果base64数据不包含data:image/前缀，添加一个默认的
      const imageSource = base64Data.startsWith('data:image/') 
        ? base64Data 
        : `data:image/jpeg;base64,${base64Data}`;
      
      return (
        <Box>
          <img 
            src={imageSource} 
            alt="消息图片" 
            style={{ 
              maxWidth: '100%', 
              maxHeight: '300px', 
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            onClick={(e) => {
              // 点击图片可以在新窗口打开
              window.open(imageSource, '_blank');
            }}
          />
        </Box>
      );
    }

    // 普通文本消息，先尝试解析XML
    const parsedMsg = parseXmlMessage(msg);
    return (
      <Box variant="p" color="text-body-secondary">
        {parsedMsg}
      </Box>
    );
  };

  return (
    <Container
      header={
        <Header
         variant="h2"
         actions={
           <SpaceBetween size="xs" direction="horizontal">
             <Box variant="small" color="text-status-inactive">
               下次刷新: {formatCountdown(countdown)}
             </Box>
             <Button variant="icon" iconName="refresh" onClick={handleRefreshClick}>刷新</Button>
           </SpaceBetween>
         }
        >
          班级群消息
        </Header>
      }
    >
      {loading ? (
        <Box textAlign="center" padding={{ vertical: 'xl' }}>
          <Spinner size="large" />
        </Box>
      ) : (
        <Box>
          {messages.length === 0 ? (
            <Box textAlign="center" padding={{ vertical: 'xl' }} color="text-body-secondary">
              暂无班级群消息
            </Box>
          ) : (
            <ColumnLayout columns={messages.length > 2 ? 3 : messages.length} variant="text-grid">
              {messages.map((chatroom) => {
                const isExpanded = expandedChatrooms[chatroom.fromWxid] !== false; // 默认展开,除非明确设为false
                const allMessages = chatroom.messages || [];
                const displayMessages = isExpanded ? allMessages : allMessages.slice(-1); // 未展开时只显示最后一条
                
                return (
                  <Container
                    key={chatroom.fromWxid}
                    header={
                      <Header
                        variant="h3"
                        actions={
                          <SpaceBetween size="xs" direction="horizontal">
                            <Badge color="blue">{chatroom.total || 0}条</Badge>
                            {allMessages.length > 1 && (
                              <Button 
                                variant="inline-icon" 
                                iconName={isExpanded ? "angle-up" : "angle-down"}
                                onClick={() => toggleChatroom(chatroom.fromWxid)}
                              >
                                {isExpanded ? '收起' : '展开'}
                              </Button>
                            )}
                          </SpaceBetween>
                        }
                      >
                        {chatroom.chatRoomName || chatroom.fromWxid}
                      </Header>
                    }
                  >
                    <SpaceBetween size="s">
                      {displayMessages.length === 0 ? (
                        <Box variant="p" color="text-body-secondary">
                          暂无消息
                        </Box>
                      ) : (
                        displayMessages.map((message, index) => (
                          <Box key={index} padding={{ vertical: 'xs' }}>
                            <SpaceBetween size="xxs">
                              {renderMessageContent(message.msg)}
                              <Box variant="small" color="text-status-inactive" textAlign="right">
                                {formatTime(message.timeStamp)}
                              </Box>
                            </SpaceBetween>
                          </Box>
                        ))
                      )}
                    </SpaceBetween>
                  </Container>
                );
              })}
            </ColumnLayout>
          )}
        </Box>
      )}
    </Container>
  );
};

export default ClassroomMessages;
